import React, { useEffect, useState } from 'react';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Collapse,
    Typography,
} from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import ReservationJobs from '../../ReservationJobs/ReservationJobs';
import FormSelect from '../../../../../shared/FormSelect';
import CustomInputLabel from '../../../../../../layout/styledcomponents/CustomInputLabel';
import { reservationEditFieldLabelSx } from '../../../EditReservation/reservationEditFieldStyles';
import { ModuleContractor } from '../../../../../types/contractor';
import { ServiceType } from '../../../../../enums/contractor';
import { SetMenu } from '../../../../../types/set-menu';
import { camelCaseToTitleCase } from '../../../../../../shared/utils';
import FormTextField from '../../../../../shared/FormTextField';

type Props = {
    experienceDetails: any;
    control: any;
    reservationDateOptions: any;

    isCatering: boolean;
    isMassage: boolean;
    isAlcohol: boolean;
    cateringDetails?: string;
    beverageDetails?: string;
    massageDetails?: string;
};

export default function AdditionalServices({
    experienceDetails,
    control,
    reservationDateOptions,
    isCatering,
    isMassage,
    isAlcohol,
    cateringDetails,
    beverageDetails,
    massageDetails,
}: Props) {
    const {
        formState: { errors },
    } = useFormContext();
    const requiresServices = useWatch({
        control,
        name: 'requiresServices',
    });

    const availableServices = new Set<string>([
        ...(experienceDetails?.contractors?.flatMap(
            (contractor: ModuleContractor) => {
                const { type, items } = contractor.services ?? {};
                if (!type) return [];
                return type === ServiceType.Other ? (items ?? []) : [type];
            },
        ) ?? []),
        ...(experienceDetails?.setMenus?.map(
            (setMenu: SetMenu) => setMenu.serviceType,
        ) ?? []),
    ]);

    const availableServicesText = Array.from(availableServices)
        .map((service) => camelCaseToTitleCase(service))
        .join(', ');

    const jobsError = (errors?.jobs as { message?: string } | undefined)
        ?.message;

    const questionFieldSx = {
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        mb: 2.5,
    } as const;

    const labelSx = { mb: 0 };
    const [additionalServicesExpanded, setAdditionalServicesExpanded] =
        useState(false);

    useEffect(() => {
        if (jobsError) {
            setAdditionalServicesExpanded(true);
        }
    }, [jobsError]);

    const hasAnyService =
        experienceDetails?.details?.catering ||
        experienceDetails?.details?.alcohol ||
        experienceDetails?.details?.massages;

    const servicesContent = (
        <>
            {availableServices.size > 0 && (
                <>
                    <Box sx={questionFieldSx}>
                        <Box>
                            <CustomInputLabel
                                htmlFor='additional-services__input'
                                id='additional-services__label'
                                sx={labelSx}
                            >
                                Do you require any additional services?
                            </CustomInputLabel>
                            {availableServicesText ? (
                                <Typography
                                    variant='body3'
                                    sx={{
                                        color: 'text.secondary',
                                        lineHeight: 1.35,
                                        fontWeight: 400,
                                    }}
                                >
                                    Available services include{' '}
                                    {availableServicesText}.
                                </Typography>
                            ) : null}
                        </Box>
                        <FormSelect
                            labelId='additional-services__label'
                            id='additional-services__input'
                            name='requiresServices'
                            control={control}
                            options={[
                                { value: true, label: 'Yes' },
                                { value: false, label: 'No' },
                            ]}
                        />
                    </Box>
                    <Collapse in={requiresServices}>
                        <Box sx={{ mb: 2.5 }}>
                            <ReservationJobs
                                experienceDetails={experienceDetails}
                                dateOptions={reservationDateOptions}
                            />
                        </Box>
                    </Collapse>
                </>
            )}

            {availableServices.size === 0 && (
                <>
                    {experienceDetails?.details?.catering && (
                        <>
                            <Box sx={questionFieldSx}>
                                <CustomInputLabel
                                    htmlFor='catering__input'
                                    id='catering__label'
                                    sx={labelSx}
                                >
                                    Do you require catering?
                                </CustomInputLabel>
                                <FormSelect
                                    labelId='catering__label'
                                    id='catering__input'
                                    name='bookingInfo.requiresCatering'
                                    control={control}
                                    options={[
                                        { value: true, label: 'Yes' },
                                        { value: false, label: 'No' },
                                    ]}
                                />
                            </Box>

                            {isCatering && (
                                <Box sx={questionFieldSx}>
                                    <Box
                                        display='flex'
                                        justifyContent='space-between'
                                    >
                                        <CustomInputLabel
                                            htmlFor='catering-details__input'
                                            sx={labelSx}
                                        >
                                            Catering Details
                                        </CustomInputLabel>
                                        {cateringDetails?.length ? (
                                            <Typography
                                                color='primary'
                                                fontSize='10px'
                                            >
                                                {`Characters left: ${
                                                    300 - cateringDetails.length
                                                }`}
                                            </Typography>
                                        ) : null}
                                    </Box>
                                    <FormTextField
                                        id='catering-details__input'
                                        name='bookingInfo.cateringNotes'
                                        placeholder='E.g. BBQ, salad and 1 person x vegan option'
                                        control={control}
                                        multiline
                                        rows={4}
                                        inputProps={{ maxLength: 300 }}
                                    />
                                </Box>
                            )}
                        </>
                    )}

                    {experienceDetails?.details?.alcohol && (
                        <>
                            <Box sx={questionFieldSx}>
                                <CustomInputLabel
                                    htmlFor='alcohol__input'
                                    id='alcohol__label'
                                    sx={labelSx}
                                >
                                    Do you require alcohol?
                                </CustomInputLabel>
                                <FormSelect
                                    labelId='alcohol__label'
                                    id='alcohol__input'
                                    name='bookingInfo.requiresBeverages'
                                    control={control}
                                    options={[
                                        { value: true, label: 'Yes' },
                                        { value: false, label: 'No' },
                                    ]}
                                />
                            </Box>

                            {isAlcohol && (
                                <Box sx={questionFieldSx}>
                                    <Box
                                        display='flex'
                                        justifyContent='space-between'
                                    >
                                        <CustomInputLabel sx={labelSx}>
                                            Alcohol Details
                                        </CustomInputLabel>
                                        {beverageDetails?.length ? (
                                            <Typography
                                                color='primary'
                                                fontSize='10px'
                                            >
                                                {`Characters left: ${
                                                    200 - beverageDetails.length
                                                }`}
                                            </Typography>
                                        ) : null}
                                    </Box>
                                    <FormTextField
                                        name='bookingInfo.beverageNotes'
                                        placeholder='E.g. Case of SuperDry, 4 White, 2 Reds'
                                        control={control}
                                        multiline
                                        rows={4}
                                        inputProps={{ maxLength: 200 }}
                                    />
                                </Box>
                            )}
                        </>
                    )}

                    {experienceDetails?.details?.massages && (
                        <>
                            <Box sx={questionFieldSx}>
                                <CustomInputLabel
                                    htmlFor='wellness__input'
                                    id='wellness__label'
                                    sx={labelSx}
                                >
                                    Do you require a wellness & lifestyle
                                    service?
                                </CustomInputLabel>
                                <FormSelect
                                    labelId='wellness__label'
                                    id='wellness__input'
                                    name='bookingInfo.requiresMassages'
                                    control={control}
                                    options={[
                                        { value: true, label: 'Yes' },
                                        { value: false, label: 'No' },
                                    ]}
                                />
                            </Box>

                            {isMassage && (
                                <Box sx={questionFieldSx}>
                                    <Box
                                        display='flex'
                                        justifyContent='space-between'
                                    >
                                        <CustomInputLabel sx={labelSx}>
                                            Notes
                                        </CustomInputLabel>
                                        {massageDetails?.length ? (
                                            <Typography
                                                color='primary'
                                                fontSize='10px'
                                            >
                                                {`Characters left: ${
                                                    200 - massageDetails.length
                                                }`}
                                            </Typography>
                                        ) : null}
                                    </Box>
                                    <FormTextField
                                        name='bookingInfo.massageNotes'
                                        placeholder='3 x full body massages and 1 x facial starting at 12pm'
                                        control={control}
                                        multiline
                                        rows={4}
                                        inputProps={{ maxLength: 200 }}
                                    />
                                </Box>
                            )}
                        </>
                    )}
                </>
            )}
        </>
    );

    return (
        <>
            {availableServices.size > 0 || hasAnyService ? (
                <Accordion
                    expanded={additionalServicesExpanded}
                    onChange={(_, next) => setAdditionalServicesExpanded(next)}
                    disableGutters
                    elevation={0}
                    sx={{
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'custom.lightGrey',
                        backgroundColor: 'background.paper',
                        overflow: 'hidden',
                        '&:before': { display: 'none' },
                    }}
                >
                    <AccordionSummary
                        expandIcon={
                            <ExpandMoreIcon sx={{ color: 'text.secondary' }} />
                        }
                        sx={{
                            px: 2,
                            py: 1.25,
                            minHeight: 56,
                            '& .MuiAccordionSummary-content': {
                                my: 0.5,
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                gap: 0.5,
                            },
                        }}
                    >
                        <Typography
                            variant='body3'
                            sx={reservationEditFieldLabelSx}
                        >
                            Additional services
                        </Typography>
                        <Typography
                            variant='body3'
                            sx={{
                                color: 'text.secondary',
                                lineHeight: 1.35,
                                fontWeight: 400,
                            }}
                        >
                            Request additional services offered by the host.
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
                        {servicesContent}
                    </AccordionDetails>
                </Accordion>
            ) : null}
        </>
    );
}
